<!-- Modal Overlay -->
<div class="modal-overlay" *ngIf="isOpen" (click)="onClose()">
  <div class="modal-container" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="modal-header">
      <h2 class="modal-title">Break Hour Settings</h2>
      <button class="close-button" (click)="onClose()" type="button">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Modal Content -->
    <div class="modal-content">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading break hour settings...</p>
      </div>

      <!-- Form Content -->
      <form [formGroup]="breakForm" (ngSubmit)="onSave()" *ngIf="!isLoading">
        <!-- Enable Break Policy Toggle -->
        <div class="toggle-section">
          <div class="toggle-container">
            <label class="toggle-label">Enable Break Policy</label>
            <div class="toggle-switch" [class.active]="enableBreakPolicy" (click)="onToggleBreakPolicy()">
              <div class="toggle-slider"></div>
            </div>
          </div>
        </div>

        <!-- Conditional Form Fields (shown when toggle is ON) -->
        <div class="form-section" *ngIf="enableBreakPolicy">
          <!-- Break Duration -->
          <div class="form-group">
            <div class="form-field">
              <label class="field-label">Break Duration (minutes)</label>
              <div class="input-container">
                <input 
                  type="number" 
                  class="form-input"
                  formControlName="breakDuration"
                  placeholder="30"
                  [class.error]="getFieldError('breakDuration')"
                  (keydown)="onNumberKeyDown($event)"
                  (input)="onNumberInput($event)"
                  (paste)="onNumberPaste($event)"
                >
                <div class="input-controls">
                <button type="button" class="control-button" (click)="incrementValue('breakDuration')">
                  <img src="/assets/images/rewards/up-arrow.svg" alt="Up" width="12" height="12">
                </button>
                <button 
                  type="button" 
                  class="control-button" 
                  [class.disabled]="isAtMinValue('breakDuration')"
                  [disabled]="isAtMinValue('breakDuration')"
                  (click)="decrementValue('breakDuration')">
                  <img src="/assets/images/rewards/down-arrow.svg" alt="Down" width="12" height="12">
                </button>
                </div>
              </div>
              <div class="error-message" *ngIf="getFieldError('breakDuration')">
                {{ getFieldError('breakDuration') }}
              </div>
            </div>
          </div>

          <!-- Work Hours Threshold -->
          <div class="form-group">
            <div class="form-field">
              <label class="field-label">Work Hours Threshold</label>
              <div class="input-container">
                <input 
                  type="number" 
                  class="form-input"
                  formControlName="workHoursThreshold"
                  placeholder="12"
                  [class.error]="getFieldError('workHoursThreshold')"
                  (keydown)="onNumberKeyDown($event)"
                  (input)="onNumberInput($event)"
                  (paste)="onNumberPaste($event)"
                >
                <div class="input-controls">
                <button type="button" class="control-button" (click)="incrementValue('workHoursThreshold')">
                  <img src="/assets/images/rewards/up-arrow.svg" alt="Up" width="12" height="12">
                </button>
                <button 
                  type="button" 
                  class="control-button" 
                  [class.disabled]="isAtMinValue('workHoursThreshold')"
                  [disabled]="isAtMinValue('workHoursThreshold')"
                  (click)="decrementValue('workHoursThreshold')">
                  <img src="/assets/images/rewards/down-arrow.svg" alt="Down" width="12" height="12">
                </button>
                </div>
              </div>
              <div class="field-description">Break after every X hours of work</div>
              <div class="error-message" *ngIf="getFieldError('workHoursThreshold')">
                {{ getFieldError('workHoursThreshold') }}
              </div>
            </div>
          </div>

          <!-- Notification Time -->
          <div class="form-group">
            <div class="form-field">
              <label class="field-label">Notification Time (minutes)</label>
              <div class="input-container">
                <input 
                  type="number" 
                  class="form-input"
                  formControlName="notificationTime"
                  placeholder="30"
                  [class.error]="getFieldError('notificationTime')"
                  (keydown)="onNumberKeyDown($event)"
                  (input)="onNumberInput($event)"
                  (paste)="onNumberPaste($event)"
                >
                <div class="input-controls">
                <button type="button" class="control-button" (click)="incrementValue('notificationTime')">
                  <img src="/assets/images/rewards/up-arrow.svg" alt="Up" width="12" height="12">
                </button>
                <button 
                  type="button" 
                  class="control-button" 
                  [class.disabled]="isAtMinValue('notificationTime')"
                  [disabled]="isAtMinValue('notificationTime')"
                  (click)="decrementValue('notificationTime')">
                  <img src="/assets/images/rewards/down-arrow.svg" alt="Down" width="12" height="12">
                </button>
                </div>
              </div>
              <div class="field-description">Notifications will be sent X minutes before break.</div>
              <div class="error-message" *ngIf="getFieldError('notificationTime')">
                {{ getFieldError('notificationTime') }}
              </div>
            </div>
          </div>

          <!-- Email Notifications Toggle -->
          <div class="toggle-section">
            <div class="toggle-container">
              <div class="toggle-info">
                <label class="toggle-label">Email Notifications</label>
                <div class="toggle-description">Send email reminders before break time.</div>
              </div>
              <div class="toggle-switch" [class.active]="breakForm.get('emailNotifications')?.value" (click)="toggleEmailNotifications()">
                <div class="toggle-slider"></div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <div class="button-group">
        <button 
          type="button" 
          class="btn btn-secondary" 
          (click)="onCancel()"
          [disabled]="isSubmitting"
        >
          Cancel
        </button>
        <button 
          type="button" 
          class="btn btn-primary" 
          [class.disabled]="!enableBreakPolicy"
          (click)="onSave()"
          [disabled]="isSubmitting || (!enableBreakPolicy && breakForm.invalid)"
        >
          <span *ngIf="!isSubmitting">Save</span>
          <span *ngIf="isSubmitting">Saving...</span>
        </button>
      </div>
    </div>
  </div>
</div>
